import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, Briefcase, Mail, Users, Eye, Plus, Database, 
  CheckCircle2, Circle, ArrowRight, Clock, ExternalLink,
  Sparkles, BarChart3, Package, Pencil, Settings, TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { adminApi } from '@/lib/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [setupProgress, setSetupProgress] = useState(0);
  const [setupChecklist, setSetupChecklist] = useState([]);
  const [showChecklist, setShowChecklist] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchAdminInfo();
    fetchRecentActivity();
  }, []);

  const fetchAdminInfo = async () => {
    try {
      const res = await adminApi.verify();
      setAdmin(res.data);
    } catch (error) {
      console.log('Could not fetch admin info');
    }
  };

  const fetchStats = async () => {
    try {
      const [statsRes, slidesRes, settingsRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getHeroSlides(),
        adminApi.getSiteSettings().catch(() => ({ data: {} }))
      ]);
      
      setStats(statsRes.data);
      const slides = slidesRes.data || [];
      const settings = settingsRes.data || {};
      
      // Calculate setup progress
      const checklistItems = [
        { id: 'slides', label: 'Add hero slides', done: slides.length > 0, link: '/admin/slides' },
        { id: 'services', label: 'Create your services', done: (statsRes.data?.total_services || 0) > 0, link: '/admin/services' },
        { id: 'blog', label: 'Write your first blog post', done: (statsRes.data?.total_posts || 0) > 0, link: '/admin/blog' },
        { id: 'testimonials', label: 'Add customer testimonials', done: (statsRes.data?.total_testimonials || 0) > 0, link: '/admin/testimonials' },
        { id: 'settings', label: 'Configure site settings', done: !!settings.site_name, link: '/admin/settings' },
        { id: 'analytics', label: 'Set up Google Analytics', done: !!settings.ga_tracking_id, link: '/admin/settings' },
      ];
      setSetupChecklist(checklistItems);
      const completedCount = checklistItems.filter(i => i.done).length;
      setSetupProgress(Math.round((completedCount / checklistItems.length) * 100));
      setShowChecklist(completedCount < checklistItems.length);
      
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const [submissionsRes, postsRes] = await Promise.all([
        adminApi.getFormSubmissions(),
        adminApi.getBlogPosts()
      ]);
      
      const submissions = (submissionsRes.data || []).slice(0, 3).map(s => ({
        id: s.id,
        type: 'submission',
        title: 'New form submission',
        description: `${s.name} sent a message`,
        time: s.created_at,
        icon: Mail,
        iconBg: 'bg-blue-100 text-blue-600',
        link: '/admin/submissions'
      }));
      
      const posts = (postsRes.data || []).slice(0, 2).map(p => ({
        id: p.id,
        type: 'post',
        title: p.is_published ? 'Blog post published' : 'Blog post drafted',
        description: p.title,
        time: p.updated_at || p.created_at,
        icon: FileText,
        iconBg: 'bg-green-100 text-green-600',
        link: `/admin/blog/edit/${p.id}`
      }));
      
      // Combine and sort by time
      const combined = [...submissions, ...posts].sort((a, b) => 
        new Date(b.time) - new Date(a.time)
      ).slice(0, 5);
      
      setRecentActivity(combined);
    } catch (error) {
      console.log('Could not fetch recent activity');
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleSeedData = async () => {
    if (!window.confirm('This will replace all existing data with sample data. Continue?')) {
      return;
    }

    setSeeding(true);
    try {
      await adminApi.seedData();
      toast.success('Sample data has been seeded successfully!');
      fetchStats();
      fetchRecentActivity();
    } catch (error) {
      toast.error('Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const statCards = [
    { 
      title: 'Total Blog Posts', 
      value: stats?.total_posts || 0, 
      published: stats?.published_posts || 0,
      icon: FileText,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      link: '/admin/blog'
    },
    { 
      title: 'Services', 
      value: stats?.total_services || 0,
      icon: Briefcase,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      link: '/admin/services'
    },
    { 
      title: 'Form Submissions', 
      value: stats?.total_submissions || 0,
      new: stats?.new_submissions || 0,
      icon: Mail,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      link: '/admin/submissions'
    },
    { 
      title: 'Testimonials', 
      value: stats?.total_testimonials || 0,
      icon: Users,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      link: '/admin/testimonials'
    },
  ];

  const quickActions = [
    { label: 'New Blog Post', icon: FileText, path: '/admin/blog/new', variant: 'default' },
    { label: 'Add Service', icon: Briefcase, path: '/admin/services', variant: 'outline' },
    { label: 'View Submissions', icon: Mail, path: '/admin/submissions', variant: 'outline' },
    { label: 'Site Settings', icon: Settings, path: '/admin/settings', variant: 'outline' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">
            {getGreeting()}, {admin?.name?.split(' ')[0] || 'there'}!
          </h1>
          <p className="text-muted-foreground mt-1">
            Here's an overview of your site. Let's make today productive!
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/" target="_blank" className="flex items-center gap-2">
              <Eye className="w-4 h-4" /> View Site
            </Link>
          </Button>
          <Button size="sm" onClick={handleSeedData} disabled={seeding} variant="outline">
            <Database className="w-4 h-4 mr-2" />
            {seeding ? 'Seeding...' : 'Seed Sample Data'}
          </Button>
        </div>
      </div>

      {/* Getting Started Checklist */}
      {showChecklist && (
        <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Getting Started</CardTitle>
                  <CardDescription>Complete these steps to set up your site</CardDescription>
                </div>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-primary">{setupProgress}%</span>
                <p className="text-xs text-muted-foreground">Complete</p>
              </div>
            </div>
            <Progress value={setupProgress} className="h-2 mt-3" />
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {setupChecklist.map((item) => (
                <Link
                  key={item.id}
                  to={item.link}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg border transition-colors
                    ${item.done 
                      ? 'bg-green-50 border-green-200 text-green-700' 
                      : 'bg-white border-border hover:border-primary/50'
                    }
                  `}
                >
                  {item.done ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <Circle className="w-5 h-5 text-muted-foreground" />
                  )}
                  <span className={`text-sm font-medium ${item.done ? 'line-through opacity-70' : ''}`}>
                    {item.label}
                  </span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Link key={stat.title} to={stat.link}>
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                    <p className="text-3xl font-bold mt-1">{stat.value}</p>
                    {stat.published !== undefined && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {stat.published} published
                      </p>
                    )}
                    {stat.new !== undefined && stat.new > 0 && (
                      <Badge className="mt-2 bg-orange-500">{stat.new} new</Badge>
                    )}
                  </div>
                  <div className={`p-3 rounded-lg ${stat.iconBg}`}>
                    <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Actions & Recent Activity Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
            <CardDescription>Common tasks at your fingertips</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {quickActions.map((action) => (
              <Button
                key={action.label}
                variant={action.variant}
                className="w-full justify-start"
                asChild
              >
                <Link to={action.path}>
                  <action.icon className="w-4 h-4 mr-2" />
                  {action.label}
                </Link>
              </Button>
            ))}
          </CardContent>
        </Card>

        {/* Recent Activity Feed */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Recent Activity</CardTitle>
                <CardDescription>Latest updates on your site</CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/admin/analytics">
                  View Analytics <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {recentActivity.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No recent activity</p>
                <p className="text-sm text-muted-foreground">Activity will appear here once you start using the CMS</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentActivity.map((activity) => (
                  <Link
                    key={`${activity.type}-${activity.id}`}
                    to={activity.link}
                    className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className={`p-2 rounded-lg ${activity.iconBg}`}>
                      <activity.icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{activity.title}</p>
                      <p className="text-sm text-muted-foreground truncate">{activity.description}</p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatTimeAgo(activity.time)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View Your Website Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-blue-900">View Your Website</h3>
              <p className="text-blue-700 text-sm">
                See how your changes look on the live site
              </p>
            </div>
            <Button asChild className="bg-blue-600 hover:bg-blue-700">
              <Link to="/" target="_blank" className="flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                View Site
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
